import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';

import { createGuestUser, getUser } from '../store/features/user/userServices';
import { useAppDispatch, useAppSelector } from '../store/reduxHooks';

export const useGuestUserInitialization = (userType: string) => {
  const dispatch = useAppDispatch();
  const { isMaintenance } = useAppSelector((state) => state.app);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        // Skip guest user initialization if maintenance mode is active
        if (isMaintenance) {
          console.log('Maintenance mode active - skipping guest user initialization');
          if (isMounted) setIsInitialized(true);
          return;
        }

        // createGuestUser signs in the guest this browser already has (see guestKey), or makes one
        if (userType === 'guest') {
          const res = await dispatch(createGuestUser());

          if (res.meta.requestStatus === 'fulfilled') {
            await dispatch(getUser());
          }
        }

        if (isMounted) setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing guest user:', error);

        if (isMounted) setIsInitialized(true);
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [userType, dispatch, isMaintenance]);

  return isInitialized;
};
// Table Hooks
export const getTableFilterItems = () => [
  { label: 'Today', name: i18n.t('timeFilter.today'), value: 'today' },
  { name: i18n.t('timeFilter.last7Days'), value: 'last7days' },
  { name: i18n.t('timeFilter.last4Weeks'), value: 'last4weeks' },
  { name: i18n.t('timeFilter.last3Months'), value: 'last3months' },
  { name: i18n.t('timeFilter.last12Months'), value: 'last12months' },
  /*}{ name: i18n.t('timeFilter.monthToDate'), value: 'monthToDate' },
  { name: i18n.t('timeFilter.quarterToDate'), value: 'quarterToDate' },
  { name: i18n.t('timeFilter.yearToDate'), value: 'yearToDate' },*/
  { name: i18n.t('timeFilter.allTime'), value: 'allTime' },
];

export const useUserPrivilege = () => {
  const { currentUser } = useAppSelector((state) => state.user);
  const { teamMembers } = useAppSelector((state) => state.team);
  const [userRole, setUserRole] = useState<string | null>(null);

  const getTeamRole = (teamMembers: any, currentUser: any) => {
    const currentUserEmail = currentUser?.email;

    // Check if teamMembers is an array before using find
    if (!Array.isArray(teamMembers)) {
      setUserRole(null);
      return;
    }

    const member = teamMembers.find((member: any) => member.email === currentUserEmail);
    setUserRole(member?.member_type || null);
  };

  useEffect(() => {
    getTeamRole(teamMembers, currentUser);
  }, [teamMembers, currentUser]);

  return userRole;
};

export const useImageWidth = (imagePath: string, imageName: string, isDesktop: boolean) => {
  return useMemo(() => {
    let pic;
    const width = isDesktop ? 2000 : 320;
    try {
      pic = require(`../assets/img/${imagePath}/${width}/${imageName}${width}.webp`);
    } catch (error) {
      try {
        // Fallback to PNG if the WebP image is not available
        pic = require(`../assets/img/${imagePath}/${width}/${imageName}${width}.png`);
      } catch (error) {
        // Fallback to SVG if the PNG image is not available
        pic = require(`../assets/img/${imagePath}/${width}/${imageName}${width}.svg`);
      }
    }

    return [{ pic, width }];
  }, [imagePath, imageName, isDesktop]);
};

export const usePreloadImages = (imageUrls: string[]) => {
  useEffect(() => {
    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, [imageUrls]);
};

export default usePreloadImages;

export const useImageForWidth = (images: { pic: string; width: number }[], width: number) => {
  return useMemo(() => {
    const sortedImages = [...images].sort((a, b) => b.width - a.width);
    const appropriateImage = sortedImages.find((image) => image.width <= width);
    // No query string: the file is served as-is, and a different URL than the preloaded one downloads it twice
    return appropriateImage ? appropriateImage.pic : sortedImages[0].pic;
  }, [images, width]);
};

export const useFormatFileName = () => {
  return useCallback((fileName: string, maxLength: number) => {
    const prefixLength = maxLength; // number of characters to show at the start
    const suffixLength = 3; // number of characters to show at the end
    const ellipsis = '...';

    if (fileName.length <= prefixLength + suffixLength) {
      return fileName;
    }

    const prefix = fileName.slice(0, prefixLength);
    const suffix = fileName.slice(-suffixLength);

    return `${prefix}${ellipsis}${suffix}`;
  }, []);
};
